import React from "react";
import { useInfiniteScroll } from "@hooks/useInfiniteScroll";
import { NoData } from "@components/common/NoData/NoData";
import type { InfiniteScrollProps } from "./types/types";
import { useTranslation } from "react-i18next";

const InfiniteScroll = <T extends { id: string | number }>({
	items,
	renderItem,
	hasNextPage,
	fetchNextPage,
	isFetching,
	isLoading,
	noDataMessage,
	className = "px-[10px] sm:px-[20px] my-8 p-5 grid lg:grid-cols-3 md:grid-cols-2 sm:grid-cols-2 grid-cols-1 gap-5",
	scrollRootId,
}: InfiniteScrollProps<T>) => {
	const lastItemRef = useInfiniteScroll(isFetching, hasNextPage, fetchNextPage, scrollRootId);
	const { t } = useTranslation();

	// Filter out undefined/null items to prevent errors
	const validItems = items.filter((item): item is T => item !== undefined && item !== null);

	// Show loading state for initial load
	if (isLoading && validItems.length === 0) {
		return (
			<div className="flex justify-center items-center py-8">
				<span>{t("psdaCommon.common.loading")}</span>
			</div>
		);
	}

	if (validItems.length === 0) {
		return <NoData msg={noDataMessage} />;
	}

	return (
		<div className={className}>
			{validItems.map((item, index) => (
				<React.Fragment key={item.id}>{renderItem(item, index)}</React.Fragment>
			))}

			{/* Separate loading indicator and intersection observer target */}
			{hasNextPage && (
				<div
					ref={lastItemRef}
					className="col-span-full flex justify-center items-center py-4"
					style={{ minHeight: "100px" }}>
					{isFetching ? (
						<span>{t("psdaCommon.common.loading")}</span>
					) : (
						<div style={{ height: "1px", width: "100%" }} />
					)}
				</div>
			)}
		</div>
	);
};

export default InfiniteScroll;
